import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { breakpoints } from 'styles';
import { Button } from 'components/common';
import {
  Checkbox,
  Form,
  FormGroup,
  RadioGroup,
  Relevant,
  Select,
  StarsSelector,
  TextArea
} from 'components/common/form';

import type { TaskAnswerReview } from 'shared-types';

interface Props {
  answerId: number;
  answerUserEmail?: string;
  courseId: number;
  gradingType: 'text' | 'text-and-stars' | 'text-and-points';
  isTaskEditable?: boolean;
  keepOpen?: boolean;
  maxPoints: number;
  onClose: () => void;
  onlyComments: boolean;
  onSuccess: () => Promise<void>;
  review?: TaskAnswerReview;
}

const ReviewForm = ({
  answerId,
  answerUserEmail,
  courseId,
  gradingType,
  isTaskEditable,
  keepOpen,
  maxPoints,
  onClose,
  onlyComments,
  onSuccess,
  review
}: Props) => {
  const { dispatch } = useDispatch();
  const { messages, serverConfig, theme } = useReduxState();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const forcedType = onlyComments
    ? 'comment'
    : review
    ? review.type
    : undefined;

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const type = forcedType || values.type;

      if (review) {
        await api.post(
          `/admin/courses/${courseId}/answer-reviews/${review.id}`,
          {
            ...values,
            type,
            AnswerId: answerId
          }
        );
      } else {
        await api.post(`/admin/courses/${courseId}/answer-reviews`, {
          ...values,
          type,
          AnswerId: answerId
        });
      }

      await onSuccess();

      setIsSubmitting(false);

      dispatch(
        actions.addNotice({
          text:
            type === 'review'
              ? messages.tasksSaveReviewSuccess
              : review
              ? messages.readerDiscussionEditCommentSuccess
              : messages.readerDiscussionAddCommentSuccess,
          type: 'success'
        })
      );

      onClose();
    } catch (err) {
      setIsSubmitting(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateRequired = (value) => {
    return !value || !value.toString().trim()
      ? messages.commonFieldRequired
      : undefined;
  };

  const initialValues = review
    ? {
        ...review.data,
        type: review.type
      }
    : { points: 0 };

  return (
    <div className="review-form">
      <Form initialValues={initialValues} onSubmit={handleSubmit}>
        {({ formState }) => (
          <div>
            <Relevant when={() => !forcedType}>
              <div className="review-type">
                <RadioGroup
                  field="type"
                  initialValue="review"
                  inline={true}
                  options={[
                    {
                      id: `review-${answerId}-${
                        review?.id ? review.id : 'create'
                      }`,
                      label: messages.tasksReview,
                      value: 'review'
                    },
                    {
                      disabled: !isTaskEditable,
                      id: `request-changes-${answerId}-${
                        review?.id ? review.id : 'create'
                      }`,
                      label: messages.tasksRequestChanges,
                      tooltip: isTaskEditable
                        ? undefined
                        : messages.tasksRequestChangesDisabled,
                      value: 'request-changes'
                    }
                  ]}
                />
              </div>
            </Relevant>

            <div className="text">
              <FormGroup
                htmlFor={`review-text-${answerId}-${
                  review?.id ? review.id : 'create'
                }`}
                label={messages.tasksReviewText}
                maxWidth="100%"
              >
                <TextArea
                  autoGrow={true}
                  error={formState.errors.text}
                  field="text"
                  id={`review-rating-${answerId}-${
                    review?.id ? review.id : 'create'
                  }`}
                  rows={3}
                  validate={validateRequired}
                  validateOnChange={true}
                />
              </FormGroup>
            </div>

            {(forcedType || formState.values.type) === 'review' &&
            (!gradingType || gradingType === 'text-and-stars') ? (
              <div className="rating">
                <div className="rating-label">{messages.tasksReviewRating}</div>

                <StarsSelector
                  error={formState.errors.stars}
                  field="stars"
                  id={`review-rating-${answerId}-${
                    review?.id ? review.id : 'create'
                  }`}
                  style={{ position: 'relative', top: 1 }}
                  validate={validateRequired}
                  validateOnChange={true}
                />
              </div>
            ) : (forcedType || formState.values.type) === 'review' &&
              gradingType === 'text-and-points' ? (
              <div className="rating">
                <div className="rating-label">{messages.commonPoints}</div>

                <Select
                  label={messages.commonPoints}
                  error={formState.errors.points}
                  field="points"
                  id={`review-rating-${answerId}-${
                    review?.id ? review.id : 'create'
                  }`}
                  options={Array.from(Array(maxPoints + 1).keys()).map(
                    (value) => ({
                      label: value,
                      value
                    })
                  )}
                  style={{ width: 100 }}
                />
              </div>
            ) : null}

            <div className="footer">
              <div className="footer-settings">
                {!review ? (
                  <Checkbox
                    disabled={!answerUserEmail}
                    field="sendEmailNotification"
                    id={`sendEmailNotification-${answerId}`}
                    initialValue={
                      answerUserEmail &&
                      serverConfig.openQuestionReviewSendNotificationDefault
                        ? true
                        : false
                    }
                    label={messages.commonSendEmailNotificationToUser}
                    labelTooltip={
                      !answerUserEmail ? messages.userNoEmail : null
                    }
                  />
                ) : null}
              </div>

              <div className="footer-buttons">
                {!keepOpen ? (
                  <Button
                    category="light"
                    onClick={onClose}
                    text={messages.commonCancel}
                  />
                ) : null}

                <Button
                  category="success"
                  loading={isSubmitting}
                  text={review ? messages.commonSave : messages.commonSubmit}
                  type="submit"
                />
              </div>
            </div>
          </div>
        )}
      </Form>

      <style jsx>{`
        .review-form {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .review-type {
          margin-bottom: 10px;
        }
        .rating {
          align-items: center;
          display: flex;
          gap: 15px;
          margin-top: 10px;
        }
        .rating-label {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
        }
        .footer {
          display: flex;
          flex-direction: column;
          gap: 15px;
          justify-content: space-between;
          margin-top: 10px;
        }
        .footer-settings {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .footer-buttons {
          display: flex;
          flex-direction: row;
          gap: 10px;
          justify-content: flex-end;
        }
        @media (min-width: ${breakpoints.small}px) {
          .footer {
            align-items: center;
            flex-direction: row;
          }
        }
      `}</style>
    </div>
  );
};

export default ReviewForm;
